/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useRef} from 'react';
import {DraggableProvidedDragHandleProps} from 'react-beautiful-dnd';
import Drawer from '@material-ui/core/Drawer';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {useMedia} from '../../hooks/useMedia';
import {
  dlsColor,
  dlsLayout,
  dlsShadow,
  dlsZIndex
} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import DraggableList from '../draggable/DraggableList';
import {useSideNavDockedDrawer} from '../navigation/useSideNavDockedDrawer';
import FilterDrawerCategoryRow from './FilterDrawerCategoryRow';
import FilterDrawerFooter from './FilterDrawerFooter';
import FilterDrawerHeader from './FilterDrawerHeader';
import {
  FilterDrawerCategoryBase,
  FilterDrawerPropsBase
} from './types';
import useFilterDrawer from './useFilterDrawer';
import {getFilterDrawerWidth} from './utils';

export interface FilterDrawerCategory extends FilterDrawerCategoryBase {}

export interface FilterDrawerProps extends FilterDrawerPropsBase {}

interface StyleProps {
  desktopOffset: number;
  isPhone: boolean;
}

// the drawer's own panel - what it covers can find it by ...
export const FILTER_DRAWER_PAPER_ID = 'filter-drawer-paper';

const useStyles = makeStyles(theme => ({
  root : {
    // this drawer used to sit at navigationDrawerPaper+1 (3005), well below
    // the tracking page's own "selected filters" bar/scrim/modal tier
    // (trackingFilterSelectedFilters*, 100001-100003) - that bar would then
    // render on top of this drawer whenever it was visible, intercepting
    // clicks meant for the drawer's own close button/backdrop. A drawer
    // must always be able to render above any of the page content behind
    // it, so this now clears that entire tier instead ...
    zIndex : `${dlsZIndex('trackingFilterSelectedFiltersConfirmation') + 1} !important` as any
  },
  drawerFocusTarget : {
    height        : 1,
    outline       : 0,
    pointerEvents : 'none',
    position      : 'absolute',
    width         : 1
  },
  paper : {
    backgroundColor : dlsColor('white'),
    borderRadius    : ({isPhone}: StyleProps) => isPhone ? '12px 12px 0 0' : '0 12px 12px 0',
    bottom          : ({isPhone}: StyleProps) => isPhone ? 0 : 'auto',
    boxShadow       : dlsShadow(3),
    display         : 'flex',
    flexDirection   : 'column',
    height          : '100vh',
    left            : ({isPhone, desktopOffset}: StyleProps) => isPhone ? 0 : desktopOffset,
    maxWidth        : ({isPhone, desktopOffset}: StyleProps) =>
      isPhone ? '100vw' : `calc(100vw - ${desktopOffset}px)`,
    top        : ({isPhone}: StyleProps) => isPhone ? 'auto' : 0,
    transition : 'left 200ms ease-in-out, transform 280ms cubic-bezier(0.2, 0, 0, 1), box-shadow 280ms ease',
    width      : ({isPhone, desktopOffset}: StyleProps) =>
      isPhone ? '100vw' : `min(${getFilterDrawerWidth()}px, calc(100vw - ${desktopOffset}px))`
  },
  header : {
    alignItems      : 'center',
    backgroundColor : dlsColor('grey6'),
    boxShadow       : '0 2px 2px rgba(0, 0, 0, 0.12)',
    boxSizing       : 'border-box',
    display         : 'flex',
    flexShrink      : 0,
    height          : 56,
    justifyContent  : 'space-between',
    padding         : '8px 16px',
    width           : '100%'
  },
  titleGroup : {
    alignItems : 'center',
    display    : 'flex',
    minWidth   : 0
  },
  title : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 16,
    fontWeight : 600,
    lineHeight : '20px',
    whiteSpace : 'nowrap'
  },
  headerActions : {
    alignItems     : 'center',
    display        : 'flex',
    gap            : 24,
    justifyContent : 'flex-end'
  },
  expandAllButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    color           : dlsColor('grey1'),
    cursor          : 'pointer',
    display         : 'flex',
    gap             : 8,
    minHeight       : 32,
    padding         : '4px 8px'
  },
  expandAllLabel : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 12,
    fontWeight : 500,
    lineHeight : '16px',
    whiteSpace : 'nowrap'
  },
  closeButton : {
    backgroundColor : dlsColor('white'),
    border          : `0.5px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxSizing       : 'border-box',
    height          : 32,
    padding         : 4,
    width           : 32,
    '&:hover'       : {
      backgroundColor : dlsColor('grey6')
    }
  },
  content : {
    flex            : 1,
    minHeight       : 0,
    overflowX       : 'hidden',
    overflowY       : 'auto',
    paddingTop      : 8,
    scrollbarGutter : 'stable'
  },
  draggableList : {
    width : '100%'
  },
  draggableItem : {
    backgroundColor : `${dlsColor('white')} !important`,
    boxSizing       : 'border-box',
    margin          : '0 !important',
    padding         : '0 !important',
    width           : '100%'
  },
  draggableItemDragging : {
    backgroundColor : `${dlsColor('white')} !important`,
    boxShadow       : dlsShadow(3),
    boxSizing       : 'border-box',
    margin          : '0 !important',
    padding         : '0 !important',
    width           : '100%'
  },
  category : {
    backgroundColor : dlsColor('white'),
    borderTop       : `2px solid ${dlsColor('paleBlue')}`,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    minHeight       : 56,
    padding         : '6px 0',
    transition      : 'background-color 160ms ease, box-shadow 180ms ease',
    width           : '100%'
  },
  categoryFirst : {
    borderTop : 0
  },
  categoryHeader : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    boxSizing       : 'border-box',
    cursor          : 'pointer',
    display         : 'flex',
    gap             : 8,
    height          : 44,
    justifyContent  : 'space-between',
    padding         : '8px 16px',
    textAlign       : 'left',
    width           : '100%'
  },
  categoryHeaderContent : {
    alignItems : 'center',
    display    : 'flex',
    flex       : 1,
    gap        : 2,
    height     : 24,
    minWidth   : 0
  },
  dragButton : {
    alignItems     : 'center',
    background     : 'transparent',
    border         : 0,
    cursor         : 'grab',
    display        : 'flex',
    flexShrink     : 0,
    height         : 24,
    justifyContent : 'center',
    padding        : 0,
    width          : 24
  },
  categoryLabel : {
    color         : dlsColor('grey2'),
    flex          : 1,
    fontFamily    : 'Roboto',
    fontSize      : 16,
    fontWeight    : 700,
    letterSpacing : 1,
    lineHeight    : '16px',
    minWidth      : 0,
    overflow      : 'hidden',
    textOverflow  : 'ellipsis',
    textTransform : 'uppercase',
    whiteSpace    : 'nowrap'
  },
  chevronButton : {
    alignItems     : 'center',
    display        : 'flex',
    flexShrink     : 0,
    height         : 24,
    justifyContent : 'center',
    width          : 24
  },
  categoryBodyShell : {
    display          : 'grid',
    gridTemplateRows : '0fr',
    opacity          : 0,
    pointerEvents    : 'none',
    transition       : 'grid-template-rows 220ms cubic-bezier(0.2, 0, 0, 1), opacity 160ms ease'
  },
  categoryBodyShellExpanded : {
    gridTemplateRows : '1fr',
    opacity          : 1,
    pointerEvents    : 'auto'
  },
  categoryBodyInner : {
    minHeight : 0,
    overflow  : 'hidden'
  },
  categoryBody : {
    color      : dlsColor('grey2'),
    fontFamily : 'Inter',
    fontSize   : 14,
    lineHeight : '20px'
  },
  footer : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    boxShadow       : '0 -2px 4px rgba(0, 0, 0, 0.12), 0 -25px 2px rgba(255, 255, 255, 0.5)',
    boxSizing       : 'border-box',
    display         : 'flex',
    flexShrink      : 0,
    height          : 64,
    padding         : '0 16px',
    width           : '100%'
  },
  // a footer carrying a message stacks it above the actions, and grows to
  // hold it rather than keeping the actions' own 64px ...
  footerWithMessage : {
    gap           : 8,
    height        : 'auto',
    padding       : '8px 16px 16px',
    alignItems    : 'stretch',
    flexDirection : 'column'
  },
  footerInner : {
    alignItems : 'center',
    display    : 'flex',
    gap        : 8,
    width      : '100%'
  },
  clearButton : {
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('pastelTeal')}`,
    borderRadius    : 8,
    color           : dlsColor('darkTeal'),
    cursor          : 'pointer',
    flex            : '0 0 204px',
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    height          : 32,
    lineHeight      : '16px',
    '&:disabled'    : {
      backgroundColor : dlsColor('grey6'),
      borderColor     : dlsColor('grey5'),
      color           : dlsColor('grey4'),
      cursor          : 'default'
    },
    [theme.breakpoints.down('xs')] : {
      flexBasis : 120
    }
  },
  applyButton : {
    alignItems      : 'center',
    backgroundColor : dlsColor('darkTeal'),
    border          : 0,
    borderRadius    : 8,
    color           : dlsColor('white'),
    cursor          : 'pointer',
    display         : 'flex',
    flex            : 1,
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    gap             : 8,
    height          : 32,
    justifyContent  : 'center',
    lineHeight      : '21px',
    minWidth        : 0,
    padding         : '4px 8px',
    '&:disabled'    : {
      backgroundColor : dlsColor('grey6'),
      color           : dlsColor('grey4'),
      cursor          : 'default'
    }
  },
  favoriteButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    color           : dlsColor('grey2'),
    cursor          : 'pointer',
    display         : 'flex',
    flexShrink      : 0,
    height          : 44,
    justifyContent  : 'center',
    padding         : 4,
    width           : 44
  },
  favoriteButtonSelected : {
    color : dlsColor('darkTeal')
  }
}));

/**
 * Displays a left-anchored filter drawer with expandable filter categories.
 *
 * ```typescript
 * import FilterDrawer from '@ion/dls/src/components/drawer/FilterDrawer';
 *
 * ...
 *
 * <FilterDrawer open={filterDrawerOpen}
 *   categories={categories}
 *   clearAllDisabled={!selectedCount}
 *   onApply={applyFilters}
 *   onClearAll={clearFilters}
 *   onClose={closeFilterDrawer} />
 * ```
 */
export const FilterDrawer = ({
  applyDisabled = false,
  categoryOrder,
  categories = [],
  clearAllDisabled = false,
  desktopOffset = dlsLayout('collapsedSidebar').width,
  expandedIds,
  favorite = false,
  favoriteState,
  footerMessage,
  onApply,
  onClearAll,
  onClose,
  onCategoryOrderChange,
  onExpandedIdsChange,
  onFavoriteChange,
  open,
  title = translate('filters:drawer.title')
}: FilterDrawerProps) => {
  const {isPhone}    = useMedia();
  const classes      = useStyles({desktopOffset, isPhone});
  const dockedDrawer = useSideNavDockedDrawer(desktopOffset);

  const {
    allExpanded,
    draggableCategories,
    expandedLookup,
    handleCategoryDragEnd,
    isFavorite,
    toggleAll,
    toggleExpanded,
    toggleFavorite
  } = useFilterDrawer({
    categoryOrder,
    categories,
    expandedIds,
    favorite,
    onCategoryOrderChange,
    onExpandedIdsChange,
    onFavoriteChange
  });

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const drawerFocusTargetRef = useRef<HTMLDivElement>(null);

  const handleDrawerEntered = useCallback(() => {
    drawerFocusTargetRef.current?.focus();
  }, []);

  const renderCategory = useCallback((
    category: FilterDrawerCategory,
    index: number,
    dragHandleProps?: DraggableProvidedDragHandleProps
  ) => (
    <FilterDrawerCategoryRow category={category}
      classes={classes}
      dragHandleProps={dragHandleProps}
      expanded={!!expandedLookup[category.id]}
      index={index}
      onToggle={toggleExpanded} />
  ), [classes, expandedLookup, toggleExpanded]);

  return (
    <Drawer anchor={isPhone ? 'bottom' : 'left'}
      open={open}
      onClose={onClose}
      classes={{root: classes.root, paper: classes.paper}}
      PaperProps={{id: FILTER_DRAWER_PAPER_ID}}
      className={dockedDrawer.className}
      disableEnforceFocus={dockedDrawer.disableEnforceFocus}
      BackdropProps={{
        className : dockedDrawer.backdropClassName,
        style     : {
          backgroundColor : 'rgba(0, 0, 0, 0.3)',
          left            : isPhone ? 0 : desktopOffset,
          top             : 0
        }
      }}
      ModalProps={{keepMounted: true}}
      SlideProps={{onEntered: handleDrawerEntered}}
      transitionDuration={{enter: 280, exit: 220}}>
      <div className={classes.drawerFocusTarget}
        ref={drawerFocusTargetRef}
        tabIndex={-1} />
      <FilterDrawerHeader allExpanded={allExpanded}
        classes={classes}
        onClose={onClose}
        onToggleAll={toggleAll}
        title={title} />
      <div className={classes.content}>
        <DraggableList items={draggableCategories}
          direction='vertical'
          listClassName={classes.draggableList}
          itemClassName={classes.draggableItem}
          draggingItemClassName={classes.draggableItemDragging}
          itemMargin={0}
          handleOnly
          renderContent={renderCategory}
          onDragEnd={handleCategoryDragEnd} />
      </div>
      <FilterDrawerFooter classes={classes}
        applyDisabled={applyDisabled}
        clearAllDisabled={clearAllDisabled}
        message={footerMessage}
        isCurrentTheme={isCurrentTheme}
        isLegacyTheme={isLegacyTheme}
        isFavorite={isFavorite}
        favoriteState={favoriteState}
        onApply={onApply}
        onClearAll={onClearAll}
        onToggleFavorite={toggleFavorite} />
    </Drawer>
  );
};

export default memo(FilterDrawer);
